import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import DataRun from '@aytm/app/stats/page/question-views/components/data-run/DataRun'

import ModeSection from '@aytm/app/stats/page/question-views/bpm/components/ModeSection'
import useBpmState from '@aytm/app/stats/page/question-views/bpm/states/useBpmState'
import {
  calcChartSelectOptions,
  calcChartSelectOptionsWithNone,
  calcProcessedData
} from '@aytm/app/stats/page/question-views/bpm/lib/model-data'

import {
  resolveComponents,
  resolveMode,
  resolvePlotBy
} from '@aytm/app/stats/page/question-views/info-view/components/charts/bpm/lib/model-data'

import { getColorTheme } from '@aytm/app/stats/lib/stat-page'
import { calcGradientColor, COLOR_GRADIENTS } from '@aytm/app/stats/lib/render/colors'

import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

// Everything the researcher sees once the question has numbers: the pinned sub-mode's own controls,
// chart and table, with no switcher between sub-modes.
const BlockBody = ({
  state,
  questionVariants,
  batches,
  showResponses,
  defaultPlotByValue,
  quadrantChartSelectOptions,
  quadrantChartSelectOptionsWithNone,
  ...props
}) => {
  const {
    id,
    questionKey,
    answer,
    components: componentsProp,
    scores,
    crossing,
    plots,
    title,
    dataLabels = 'hover',
    xAxisLimits,
    yAxisLimits,
    chartMode,
    chartLegendMode,
    features,
    onChartReady
  } = props

  const components = resolveComponents(componentsProp)

  // everything the sub-mode's controls, chart and table draw from
  const modeProps = {
    ...state,
    // a sub-mode the researcher has not opened yet holds no selection
    tvBrandsValue: state.tvBrandsValue || [],
    tvAttributesValue: state.tvAttributesValue || [],
    mdsBrandsValue: state.mdsBrandsValue || [],
    mdsAttributesValue: state.mdsAttributesValue || [],
    id,
    questionKey,
    questionVariants,
    batches,
    answer,
    showResponses,
    defaultPlotByValue,
    scores,
    crossing,
    plots,
    title,
    dataLabels,
    xAxisLimits,
    yAxisLimits,
    quadrantChartSelectOptions,
    quadrantChartSelectOptionsWithNone,
    colorTheme: getColorTheme(),
    colorGradient: calcGradientColor,
    colorGradients: COLOR_GRADIENTS,
    chartMode,
    chartLegendMode,
    features,
    onChartReady
  }

  return <ModeSection {...modeProps} components={components} switcher={null} />
}

BlockBody.displayName = 'InfoViewBpmBlockBody'

// An info-view perceptual-mapping chart: one sub-mode of the question block, pinned by the view
// the report author placed. It draws the same run panel, controls, chart and table the question
// page does, but never the switcher between sub-modes - the chart is what it is.
//
// A view that names no sub-mode has nothing to draw; the run panel is then all there is.
const Block = props => {
  const {
    questionVariants: questionVariantsProp,
    batches: batchesProp,
    answer,
    type,
    view,
    calcBadData,
    qLogicVisibility,
    qAliasesVisibility,
    questionKey,
    schemaKey,
    defaultPlotByValue: defaultPlotByValueProp,
    params,
    paramsRevision,
    onReady,
    onStateChange,
    onBadData
  } = props

  const { questionVariants, batches, showResponses } = useMemo(
    () =>
      calcProcessedData({
        questionVariants: questionVariantsProp,
        batches: batchesProp,
        qLogicVisibility,
        qAliasesVisibility
      }),
    [questionVariantsProp, batchesProp, qLogicVisibility, qAliasesVisibility]
  )

  const mode = resolveMode({ type, view })
  const defaultPlotByValue = resolvePlotBy({ type, view, defaultPlotByValue: defaultPlotByValueProp })

  const modes = useMemo(() => [mode].filter(Boolean), [mode])

  const quadrantChartSelectOptions = useMemo(() => calcChartSelectOptions(batches), [batches])
  const quadrantChartSelectOptionsWithNone = useMemo(() => calcChartSelectOptionsWithNone(batches), [batches])

  // the api the block hands up is owed on mount, so the state lives here rather than in the body
  const state = useBpmState({
    params,
    paramsRevision,
    modes,
    defaultMode: mode,
    defaultPlotByValue,
    questionVariants,
    batches,
    selectOptions: quadrantChartSelectOptions,
    selectOptionsWithNone: quadrantChartSelectOptionsWithNone,
    // a chart with no sub-mode has no state worth persisting, so it never hands an api up
    onReady: mode ? onReady : undefined,
    onStateChange: mode ? onStateChange : undefined
  })

  // the question's own rule, applied to the payload on screen -- read here rather than taken as a
  // flag, so what the block draws can never disagree with what it was handed
  const badData = Boolean(calcBadData?.(answer))

  return (
    <BlockWrapper>
      <DataRun
        answer={answer}
        badData={badData}
        type={type}
        questionKey={questionKey}
        schemaKey={schemaKey}
        calcBadData={calcBadData}
        onBadData={onBadData}
      />
      {/* no switcher: the view is pinned to the sub-mode its author placed */}
      {badData || !mode ? null : (
        <BlockBody
          {...props}
          state={state}
          questionVariants={questionVariants}
          batches={batches}
          showResponses={showResponses}
          defaultPlotByValue={defaultPlotByValue}
          quadrantChartSelectOptions={quadrantChartSelectOptions}
          quadrantChartSelectOptionsWithNone={quadrantChartSelectOptionsWithNone}
        />
      )}
    </BlockWrapper>
  )
}

Block.displayName = 'InfoViewBpmBlock'

Block.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  questionVariants: PropTypes.array,
  batches: PropTypes.array,
  answer: answerPropType,
  type: PropTypes.string,
  view: PropTypes.string,
  calcBadData: PropTypes.func,
  qLogicVisibility: PropTypes.string,
  qAliasesVisibility: PropTypes.string,
  schemaKey: PropTypes.string,
  components: PropTypes.object,
  defaultPlotByValue: PropTypes.string,
  scores: PropTypes.array,
  crossing: PropTypes.object,
  plots: PropTypes.array,
  title: PropTypes.string,
  dataLabels: PropTypes.string,
  xAxisLimits: PropTypes.object,
  yAxisLimits: PropTypes.object,
  params: PropTypes.shape({ bpm: PropTypes.string, s: PropTypes.string }),
  paramsRevision: PropTypes.number,
  chartMode: PropTypes.string,
  chartLegendMode: PropTypes.string,
  features: PropTypes.object,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onBadData: PropTypes.func,
  onChartReady: PropTypes.func
}

// the body is handed the block's own props, plus the state and the resolved values the block owns
BlockBody.propTypes = {
  ...Block.propTypes,
  state: PropTypes.object,
  showResponses: PropTypes.bool,
  quadrantChartSelectOptions: PropTypes.array,
  quadrantChartSelectOptionsWithNone: PropTypes.array
}

export default Block
